import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Settings, Grid3X3, Tag, ThumbsUp, 
  Image, BarChart3, MousePointer, MessageSquare, Palette,
  ChevronDown, ChevronUp, Plus, Trash2, ExternalLink, ArrowUpDown, Zap, Share2, Bot, MessageCircle, Moon, Calculator, Search
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import ImageUploader from '../../components/ImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Collapsible Section Component
const ConfigSection = ({ icon: Icon, title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-royal/10 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-royal" />
          </div>
          <h2 className="font-fredoka text-lg font-bold text-slate-900">{title}</h2>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && <div className="p-4 pt-0 border-t border-slate-100">{children}</div>}
    </div>
  );
};

const AdminConfiguracion = () => {
  const { token } = useAuth();
  const { settings, updateSettings, fetchSettings } = useSite();
  const [saving, setSaving] = useState(false);
  const [quickButtons, setQuickButtons] = useState([]);
  const [bannerButtons, setBannerButtons] = useState([]);
  const [headerButtons, setHeaderButtons] = useState([]);
  const [couponTabs, setCouponTabs] = useState([]);
  const [helpBlocks, setHelpBlocks] = useState([]);
  const [customTags, setCustomTags] = useState([]);
  const [offerCategories, setOfferCategories] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [config, setConfig] = useState({
    site_name: 'Ofertopolis',
    logo_url: '',
    whatsapp_link: '',
    facebook_link: '',
    affiliate_link: 'https://mercadolibre.com/sec/1ppLzNT',
    grid_desktop_columns: 3,
    grid_tablet_columns: 2,
    grid_mobile_columns: 2,
    coupon_sort_by: 'recent',
    custom_tags: '[]',
    show_worked_button: true,
    show_not_worked_button: false,
    banner_title: 'Cupones de Descuento',
    banner_subtitle: 'Ahorra en tus compras con cupones verificados',
    banner_image_url: '',
    banner_bg_color: '#fbbf24',
    banner_gradient_from: '#fbbf24',
    banner_gradient_to: '#f59e0b',
    banner_height: 'auto',
    banner_facebook_count: '',
    banner_whatsapp_count: '',
    banner_coupons_month: '',
    banner_facebook_url: '',
    banner_whatsapp_url: '',
    banner_extra_buttons: '[]',
    banner_buttons_size: 'small',
    banner_buttons_bold: false,
    banner_facebook_color: '',
    banner_whatsapp_color: '',
    banner_text_bold: false,
    banner_image_size: 'small',
    banner_subtitle_enabled: true,
    banner_title_color: '#000000',
    banner_subtitle_color: '#000000',
    // Coupon Finder (calculator)
    finder_enabled: true,
    finder_emoji: '',
    finder_placeholder: '¿Cuánto cuesta tu compra?',
    finder_button_text: 'Encontrar',
    finder_button_bg: '#3B82F6',
    finder_button_text_color: '#ffffff',
    finder_image_url: '/api/uploads/panda-mascot.png',
    finder_best_badge_text: 'MEJOR OPCIÓN',
    finder_best_badge_bg: '#10b981',
    finder_best_badge_text_color: '#ffffff',
    finder_border_color: '#10b981',
    finder_savings_text_color: '#2563eb',
    finder_savings_suffix: 'de ahorro',
    finder_copy_button_text: 'Copiar y canjear',
    finder_copy_button_bg: '#fbbf24',
    finder_copy_button_text_color: '#000000',
    finder_hint_bg: '#EFF6FF',
    finder_hint_text_color: '#1e293b',
    finder_hint_accent_color: '#2563eb',
    finder_hint_gap_text: 'Solo agrega {gap} más para el cupón de compra mínima de {min}',
    finder_hint_extra_text: 'Productos extra por sólo {gap}',
    finder_no_result_text: 'No encontramos un cupón para ese monto. Prueba con un monto mayor.',
    finder_bank_combo_text: '💳 Combínalo con un cupón bancario si tienes disponible',
    finder_bank_combo_bg: '#FEF3C7',
    finder_bank_combo_text_color: '#78350f',
    help_button_enabled: true,
    help_button_text: '❓ ¿Cómo usar los cupones?',
    help_button_color: '#2962FF',
    help_title: 'Cómo usar los cupones',
    help_blocks: '[]',
    chat_room_enabled: true,
    chat_room_emoji: '💬',
    chat_room_color: '#2962FF',
    chat_room_title: 'Chat de la comunidad',
    chat_room_cooldown: 5,
    dark_mode_enabled: true,
    dark_mode_default: false,
    dark_bg_color: '#0f172a',
    dark_card_bg_color: '#1e293b',
    dark_text_color: '#f1f5f9',
    dark_header_color: '#1e293b',
    header_nav_buttons: '[]',
    header_bg_color: '#FFD600',
    header_text_color: '#0f172a',
    action_emoji_worked: '👍',
    action_emoji_not_worked: '👎',
    savings_bar_enabled: true,
    savings_bar_title: 'Ahorro de la comunidad',
    savings_bar_text: 'Ahorro estimado generado usando los cupones',
    savings_goal: 100000,
    savings_initial: 0,
    savings_emoji_progress: '🐼',
    savings_emoji_goal: '🎋',
    savings_panda_img_open: '',
    savings_panda_img_closed: '',
    savings_path_emojis: '🪙',
    savings_path_emoji_count: 8,
    savings_bar_color: '#10b981',
    savings_show_amount: true,
    savings_show_text: true,
    savings_period: 'total',
    savings_auto_reset: false,
    coupon_tabs: '[]',
    banner_show_description_mobile: true,
    quick_access_buttons: '[]',
    // Ofertas search bar
    offers_search_placeholder: 'Describe lo que buscas. Ej: cel bueno con buena cámara que no pase de 5 mil',
    offers_search_emoji: '✨',
    offers_bar_bg_color: '#ffffff',
    offers_bar_border_color: '#e2e8f0',
    offers_bar_button_bg: '#2563eb',
    offers_bar_button_text: '#ffffff',
    offers_suggestions_mode: 'manual',
    ml_search_url_template: 'https://listado.mercadolibre.com.mx/{q}',
    ml_affiliate_profile_url: '',
    show_copies_count: true,
    show_validations_count: true,
    show_publish_date: false,
    show_update_date: false,
    show_coupon_status: false,
    button_text: 'Copiar y Canjear',
    product_button_text: 'Ver Oferta',
    button_redirect_seconds: 5,
    button_show_animation: true,
    button_copied_message: '¡Código copiado!',
    button_redirect_message: 'Pégalo en la sección de cupones de Mercado Libre',
    whatsapp_share_message: '🎉 ¡Mira este cupón de descuento!\n\n🏷️ {title}\n💰 Código: {code}\n\n📋 Instrucciones:\n1. Copia el código: {code}\n2. Ve al link: {affiliate_link}\n3. Pega el código en la sección de cupones\n\n🔗 Más cupones en: {site_url}',
    msg_no_coupons: 'No hay cupones disponibles',
    msg_no_coupons_desc: 'Pronto agregaremos nuevos cupones de descuento. ¡Vuelve pronto!',
    msg_welcome: '',
    msg_validation_thanks: '¡Gracias por tu voto!',
    msg_already_used: 'Ya usaste este cupón',
    default_coupon_bg_color: '#ffffff',
    default_coupon_discount_bg: '#4f46e5',
    default_coupon_button_bg: '#fbbf24',
    default_coupon_button_text: '#1e293b',
    default_coupon_text_color: '#1e293b',
    default_coupon_min_purchase_bg: '#fef3c7',
    default_coupon_border_color: '#FFD600',
    // Neon glow (separate for border and button) + pulse when <=1h
    neon_border_enabled: false,
    neon_border_color: '#00E5FF',
    neon_button_enabled: false,
    neon_button_color: '#00E5FF',
    neon_pulse_enabled: true,
    upcoming_enabled: false,
    upcoming_route_steps: 'Más → Cupones →',
    upcoming_route_highlight: 'Banner 10/10 con Belinda',
    // Chatbot
    chatbot_enabled: true,
    chatbot_welcome_message: '¡Hola! 👋 Soy el asistente de Ofertopolis. Puedo ayudarte con información sobre cupones, descuentos y cómo usarlos. ¿En qué puedo ayudarte?',
    chatbot_faqs: '[]',
    chatbot_name: 'Asistente Ofertopolis',
    chatbot_additional_info: '',
    chatbot_fallback_message: 'Lo siento, no tengo información sobre eso. Pero puedo ayudarte con cupones de descuento, cómo usarlos, o información de nuestra página.'
  });
  const [chatbotFaqs, setChatbotFaqs] = useState([]);
  const [chatStats, setChatStats] = useState(null);
  const [savingsStats, setSavingsStats] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);

  const fetchChatRoomData = async () => {
    try {
      const [msgs, blocked] = await Promise.all([
        axios.get(`${API}/chat-room/messages`),
        axios.get(`${API}/admin/chat-room/blocked`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setChatMessages(msgs.data.slice().reverse());
      setBlockedUsers(blocked.data);
    } catch (error) {
      console.error('Error fetching chat room:', error);
    }
  };

  const handleDeleteChatMessage = async (mid) => {
    try {
      await axios.delete(`${API}/admin/chat-room/messages/${mid}`, { headers: { Authorization: `Bearer ${token}` } });
      setChatMessages(prev => prev.filter(m => m.id !== mid));
      toast.success('Mensaje eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleClearChat = async () => {
    if (!window.confirm('¿Borrar TODOS los mensajes del chat?')) return;
    try {
      await axios.delete(`${API}/admin/chat-room/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setChatMessages([]);
      toast.success('Chat vaciado');
    } catch (error) {
      toast.error('Error al vaciar el chat');
    }
  };

  const handleBlockChatUser = async (user_id, username) => {
    try {
      await axios.post(`${API}/admin/chat-room/block`, { user_id, username }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${username} bloqueado`);
      fetchChatRoomData();
    } catch (error) {
      toast.error('Error al bloquear');
    }
  };

  const handleUnblockChatUser = async (user_id) => {
    try {
      await axios.post(`${API}/admin/chat-room/unblock`, { user_id }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Usuario desbloqueado');
      fetchChatRoomData();
    } catch (error) {
      toast.error('Error al desbloquear');
    }
  };

  const fetchSavingsStats = async () => {
    try {
      const response = await axios.get(`${API}/savings`);
      setSavingsStats(response.data);
    } catch (error) {
      console.error('Error fetching savings:', error);
    }
  };

  const handleResetSavings = async () => {
    if (!window.confirm('¿Reiniciar el progreso de la barra de ahorro? El histórico se conserva en las estadísticas.')) return;
    try {
      await axios.post(`${API}/admin/savings/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Progreso reiniciado');
      fetchSavingsStats();
    } catch (error) {
      toast.error('Error al reiniciar el progreso');
    }
  };

  useEffect(() => {
    setConfig(prev => ({ ...prev, ...settings }));
    // Parse quick buttons
    try {
      const buttons = JSON.parse(settings.quick_access_buttons || '[]');
      setQuickButtons(buttons.length > 0 ? buttons : []);
    } catch (e) {
      setQuickButtons([]);
    }
    // Parse banner extra buttons
    try {
      const bButtons = JSON.parse(settings.banner_extra_buttons || '[]');
      setBannerButtons(bButtons.length > 0 ? bButtons : []);
    } catch (e) {
      setBannerButtons([]);
    }
    // Parse header nav buttons
    try {
      const hButtons = JSON.parse(settings.header_nav_buttons || '[]');
      setHeaderButtons(hButtons.length > 0 ? hButtons : []);
    } catch (e) {
      setHeaderButtons([]);
    }
    // Parse help blocks
    try {
      const hBlocks = JSON.parse(settings.help_blocks || '[]');
      setHelpBlocks(hBlocks.length > 0 ? hBlocks : []);
    } catch (e) {
      setHelpBlocks([]);
    }
    // Parse coupon tabs
    const defaultTabs = [
      { id: 'store', emoji: '🛒', name: 'Tienda', color: '#FFD600', text_color: '#0f172a' },
      { id: 'bank', emoji: '🏦', name: 'Bancarios', color: '#2962FF', text_color: '#ffffff' }
    ];
    try {
      const tabs = JSON.parse(settings.coupon_tabs || '[]');
      setCouponTabs(tabs.length > 0 ? tabs : defaultTabs);
    } catch (e) {
      setCouponTabs(defaultTabs);
    }
    // Parse custom tags
    try {
      const tags = JSON.parse(settings.custom_tags || '[]');
      setCustomTags(tags.length > 0 ? tags : [
        { id: '1', emoji: '✨', text: 'Nuevo', color: '#10b981', min_copies: 0, min_likes: 0, priority: 1, active: true },
        { id: '2', emoji: '🔥', text: 'Popular', color: '#f59e0b', min_copies: 10, min_likes: 0, priority: 2, active: true },
        { id: '3', emoji: '🏆', text: 'Top', color: '#8b5cf6', min_copies: 0, min_likes: 5, priority: 3, active: true },
      ]);
    } catch (e) {
      setCustomTags([
        { id: '1', emoji: '✨', text: 'Nuevo', color: '#10b981', min_copies: 0, min_likes: 0, priority: 1, active: true },
        { id: '2', emoji: '🔥', text: 'Popular', color: '#f59e0b', min_copies: 10, min_likes: 0, priority: 2, active: true },
        { id: '3', emoji: '🏆', text: 'Top', color: '#8b5cf6', min_copies: 0, min_likes: 5, priority: 3, active: true },
      ]);
    }
    // Parse offer categories
    try {
      const cats = JSON.parse(settings.offer_categories || '[]');
      setOfferCategories(Array.isArray(cats) ? cats : []);
    } catch (e) {
      setOfferCategories([]);
    }
    // Parse AI search suggestion chips
    try {
      const sugg = JSON.parse(settings.offers_search_suggestions || '[]');
      setSearchSuggestions(Array.isArray(sugg) ? sugg : []);
    } catch (e) {
      setSearchSuggestions([]);
    }
    // Parse chatbot FAQs
    try {
      const faqs = JSON.parse(settings.chatbot_faqs || '[]');
      setChatbotFaqs(faqs.length > 0 ? faqs : []);
    } catch (e) {
      setChatbotFaqs([]);
    }
  }, [settings]);

  // Fetch chat statistics
  const fetchChatStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/chat-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatStats(response.data);
    } catch (error) {
      console.error('Error fetching chat stats:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchChatStats();
      fetchSavingsStats();
      fetchChatRoomData();
    }
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSave = {
        ...config,
        quick_access_buttons: JSON.stringify(quickButtons),
        banner_extra_buttons: JSON.stringify(bannerButtons),
        header_nav_buttons: JSON.stringify(headerButtons),
        coupon_tabs: JSON.stringify(couponTabs),
        help_blocks: JSON.stringify(helpBlocks),
        custom_tags: JSON.stringify(customTags),
        offer_categories: JSON.stringify(offerCategories),
        offers_search_suggestions: JSON.stringify(searchSuggestions.map(s => (s || '').trim()).filter(Boolean)),
        chatbot_faqs: JSON.stringify(chatbotFaqs)
      };
      await axios.put(`${API}/settings`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateSettings(dataToSave);
      await fetchSettings();
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Quick Buttons handlers
  const addQuickButton = () => {
    const newButton = {
      id: Date.now().toString(),
      text: 'Nuevo Botón',
      url: '',
      color: '#3b82f6',
      icon: '🔗',
      order: quickButtons.length + 1,
      active: true
    };
    setQuickButtons([...quickButtons, newButton]);
  };

  const updateQuickButton = (id, field, value) => {
    setQuickButtons(quickButtons.map(btn => 
      btn.id === id ? { ...btn, [field]: value } : btn
    ));
  };

  const removeQuickButton = (id) => {
    setQuickButtons(quickButtons.filter(btn => btn.id !== id));
  };

  // Banner extra buttons handlers
  const addBannerButton = () => {
    setBannerButtons([...bannerButtons, { id: Date.now().toString(), emoji: '⭐', text: 'Nuevo Botón', url: '', color: '#3b82f6' }]);
  };

  const updateBannerButton = (id, field, value) => {
    setBannerButtons(bannerButtons.map(btn => btn.id === id ? { ...btn, [field]: value } : btn));
  };

  const removeBannerButton = (id) => {
    setBannerButtons(bannerButtons.filter(btn => btn.id !== id));
  };

  // Header nav buttons handlers
  const addHeaderButton = () => {
    setHeaderButtons([...headerButtons, { id: Date.now().toString(), emoji: '🔗', text: 'Nuevo Botón', url: '', bg_color: '#ffffff', text_color: '#1e293b', bold: false, font: 'nunito' }]);
  };

  const updateHeaderButton = (id, field, value) => {
    setHeaderButtons(headerButtons.map(btn => btn.id === id ? { ...btn, [field]: value } : btn));
  };

  const removeHeaderButton = (id) => {
    setHeaderButtons(headerButtons.filter(btn => btn.id !== id));
  };

  // Coupon tabs handlers
  const addCouponTab = () => {
    setCouponTabs([...couponTabs, { id: `tab_${Date.now()}`, emoji: '📌', name: 'Nueva Pestaña', color: '#10b981', text_color: '#ffffff' }]);
  };

  const updateCouponTab = (id, field, value) => {
    setCouponTabs(couponTabs.map(tab => tab.id === id ? { ...tab, [field]: value } : tab));
  };

  const removeCouponTab = (id) => {
    if (couponTabs.length <= 1) {
      toast.error('Debe existir al menos una pestaña');
      return;
    }
    setCouponTabs(couponTabs.filter(tab => tab.id !== id));
  };

  // Help blocks handlers
  const addHelpBlock = (type) => {
    setHelpBlocks(prev => [...prev, { id: Date.now().toString(), type, content: '' }]);
  };

  const updateHelpBlock = (id, content) => {
    setHelpBlocks(prev => prev.map(b => b.id === id ? { ...b, content } : b));
  };

  const removeHelpBlock = (id) => {
    setHelpBlocks(prev => prev.filter(b => b.id !== id));
  };

  const moveHelpBlock = (id, dir) => {
    setHelpBlocks(prev => {
      const idx = prev.findIndex(b => b.id === id);
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  // Custom Tags handlers
  const addCustomTag = () => {
    const newTag = {
      id: Date.now().toString(),
      emoji: '🏷️',
      text: 'Nueva Etiqueta',
      color: '#6366f1',
      min_copies: 0,
      min_likes: 0,
      priority: customTags.length + 1,
      active: true
    };
    setCustomTags([...customTags, newTag]);
  };

  const updateCustomTag = (id, field, value) => {
    setCustomTags(customTags.map(tag => 
      tag.id === id ? { ...tag, [field]: value } : tag
    ));
  };

  const removeCustomTag = (id) => {
    setCustomTags(customTags.filter(tag => tag.id !== id));
  };

  // Offer Categories handlers (chips/tabs shown only in the Ofertas page)
  const addOfferCategory = () => {
    setOfferCategories([...offerCategories, {
      id: Date.now().toString(),
      name: 'Nueva categoría',
      mode: 'manual', // 'manual' = assign offers by hand | 'price' = auto by max price
      maxPrice: 0,
    }]);
  };

  const updateOfferCategory = (id, field, value) => {
    setOfferCategories(offerCategories.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const removeOfferCategory = (id) => {
    setOfferCategories(offerCategories.filter(c => c.id !== id));
  };

  // AI search suggestion chips handlers
  const addSearchSuggestion = () => setSearchSuggestions([...searchSuggestions, '']);
  const updateSearchSuggestion = (index, value) => setSearchSuggestions(searchSuggestions.map((s, i) => i === index ? value : s));
  const removeSearchSuggestion = (index) => setSearchSuggestions(searchSuggestions.filter((_, i) => i !== index));

  // Chatbot FAQ handlers
  const addFaq = () => {
    const newFaq = {
      id: Date.now().toString(),
      question: '',
      answer: ''
    };
    setChatbotFaqs([...chatbotFaqs, newFaq]);
  };

  const updateFaq = (id, field, value) => {
    setChatbotFaqs(chatbotFaqs.map(faq => 
      faq.id === id ? { ...faq, [field]: value } : faq
    ));
  };

  const removeFaq = (id) => {
    setChatbotFaqs(chatbotFaqs.filter(faq => faq.id !== id));
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 pb-24" data-testid="admin-configuracion">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="p-2 bg-white rounded-lg shadow-sm hover:bg-slate-50">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="font-fredoka text-xl sm:text-2xl font-bold text-slate-900">Configuración</h1>
            <p className="text-slate-600 text-xs sm:text-sm">Panel de control completo</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-royal text-white hover:bg-royal/90">
          {saving ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      <div className="max-w-4xl space-y-4">
        
        {/* Próximos cupones Section */}
        <ConfigSection icon={Zap} title="Próximos cupones" defaultOpen={true}>
          <div className="space-y-3 pt-4">
            <div className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50">
              <div>
                <Label className="text-sm font-semibold">Mostrar sección "Próximos cupones" ⚡</Label>
                <p className="text-xs text-slate-500">Activa o desactiva por completo la sección y su pestaña en el menú.</p>
              </div>
              <Switch
                data-testid="upcoming-enabled-switch"
                checked={!!config.upcoming_enabled}
                onCheckedChange={(v) => updateConfig('upcoming_enabled', v)}
              />
            </div>
            <p className="text-xs text-slate-500">
              Los cupones de esta sección se crean y administran en <strong>Admin → Cupones → pestaña "⚡ Próximos"</strong>.
            </p>

            <div className="p-3 rounded-xl border border-slate-200 bg-white space-y-3">
              <p className="text-sm font-semibold text-slate-700">Ruta del pop-up "Ir al cupón" 🎟️</p>
              <p className="text-xs text-slate-500">Lo que ve el usuario antes de abrir Mercado Libre. Edítalo si cambia el banner/promoción.</p>
              <div className="space-y-1.5">
                <Label className="text-xs">Pasos (primera línea)</Label>
                <Input
                  value={config.upcoming_route_steps || ''}
                  onChange={(e) => updateConfig('upcoming_route_steps', e.target.value)}
                  placeholder="Más → Cupones →"
                  data-testid="upcoming-route-steps-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Destino resaltado (segunda línea)</Label>
                <Input
                  value={config.upcoming_route_highlight || ''}
                  onChange={(e) => updateConfig('upcoming_route_highlight', e.target.value)}
                  placeholder="Banner 10/10 con Belinda"
                  data-testid="upcoming-route-highlight-input"
                />
              </div>
            </div>
          </div>
        </ConfigSection>

        {/* Custom Tags Section */}
        <ConfigSection icon={Tag} title="Etiquetas Personalizadas" defaultOpen={true}>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-slate-600 mb-4">
              Define etiquetas que aparecerán en los cupones. Usa "Máx. Horas" para etiquetas basadas en tiempo (ej: "Nuevo" = cupones de últimas 48 horas).
            </p>
            
            {customTags.map((tag) => (
              <div key={tag.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span 
                      className="px-3 py-1 rounded-full text-white text-sm font-bold"
                      style={{ backgroundColor: tag.color }}
                    >
                      {tag.emoji} {tag.text}
                    </span>
                    <Switch 
                      checked={tag.active !== false} 
                      onCheckedChange={(v) => updateCustomTag(tag.id, 'active', v)}
                    />
                    {tag.max_hours_old > 0 && (
                      <span className="text-xs text-slate-500 bg-slate-200 px-2 py-0.5 rounded">
                        ⏱️ Por tiempo
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => removeCustomTag(tag.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Emoji</Label>
                    <Input 
                      value={tag.emoji} 
                      onChange={(e) => updateCustomTag(tag.id, 'emoji', e.target.value)}
                      className="text-center text-lg"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Texto</Label>
                    <Input 
                      value={tag.text} 
                      onChange={(e) => updateCustomTag(tag.id, 'text', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Máx. Horas</Label>
                    <Input 
                      type="number" 
                      min="0"
                      placeholder="0 = desactivado"
                      value={tag.max_hours_old || ''} 
                      onChange={(e) => updateCustomTag(tag.id, 'max_hours_old', parseInt(e.target.value) || 0)}
                    />
                    <p className="text-[10px] text-slate-400 mt-0.5">Para "Nuevo" usa 24-48</p>
                  </div>
                  <div>
                    <Label className="text-xs">Mín. Copias</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={tag.min_copies} 
                      onChange={(e) => updateCustomTag(tag.id, 'min_copies', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mín. Likes</Label>
                    <Input 
                      type="number" 
                      min="0"
                      value={tag.min_likes} 
                      onChange={(e) => updateCustomTag(tag.id, 'min_likes', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Color</Label>
                    <div className="flex items-center gap-2">
                      <input 
                        type="color" 
                        value={tag.color}
                        onChange={(e) => updateCustomTag(tag.id, 'color', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input 
                        value={tag.color}
                        onChange={(e) => updateCustomTag(tag.id, 'color', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="w-24">
                    <Label className="text-xs">Prioridad</Label>
                    <Input 
                      type="number" 
                      min="1"
                      value={tag.priority || 1} 
                      onChange={(e) => updateCustomTag(tag.id, 'priority', parseInt(e.target.value) || 1)}
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <Button onClick={addCustomTag} variant="outline" className="w-full">
              <Plus className="w-4 h-4 mr-2" /> Agregar Etiqueta
            </Button>
          </div>
        </ConfigSection>

        {/* Offer Categories Section (only shown in the Ofertas page) */}
        <ConfigSection icon={Tag} title="Categorías de Ofertas">
          <div className="space-y-4 pt-4">
            <p className="text-sm text-slate-600 mb-2">
              Crea las categorías que aparecen como chips arriba en la página de <strong>Ofertas</strong>.
              Elige "Manual" para asignar tú mismo las ofertas a esta categoría, o "Por precio" para incluir
              automáticamente las ofertas cuyo precio (con descuento) sea menor o igual al monto que definas.
            </p>

            {offerCategories.map((cat) => (
              <div key={cat.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="px-3 py-1 rounded-full bg-blue-600 text-white text-sm font-bold">
                    {cat.name || 'Sin nombre'}
                  </span>
                  <button
                    onClick={() => removeOfferCategory(cat.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Nombre</Label>
                    <Input
                      value={cat.name}
                      onChange={(e) => updateOfferCategory(cat.id, 'name', e.target.value)}
                      placeholder="Ej: Tenis, Tecnología..."
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <select
                      value={cat.mode || 'manual'}
                      onChange={(e) => updateOfferCategory(cat.id, 'mode', e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                    >
                      <option value="manual">Manual (asignar ofertas)</option>
                      <option value="price">Por precio (automática)</option>
                    </select>
                  </div>
                  {cat.mode === 'price' && (
                    <div>
                      <Label className="text-xs">Precio máximo ($)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={cat.maxPrice || ''}
                        onChange={(e) => updateOfferCategory(cat.id, 'maxPrice', parseFloat(e.target.value) || 0)}
                        placeholder="Ej: 500"
                      />
                    </div>
                  )}
                </div>
                {cat.mode === 'manual' && (
                  <p className="text-[11px] text-slate-400">
                    Asigna ofertas a esta categoría desde Admin → Cupones, al crear/editar una oferta.
                  </p>
                )}
              </div>
            ))}

            <Button onClick={addOfferCategory} variant="outline" className="w-full">
              <Plus className="w-4 h-4 mr-2" /> Agregar Categoría de Oferta
            </Button>
          </div>
        </ConfigSection>

        {/* Ofertas Search Bar */}
        <ConfigSection icon={Search} title="Buscador de Ofertas">
          <div className="space-y-5 pt-4">
            <p className="text-sm text-slate-600">
              Personaliza la barra del buscador inteligente de la página de <strong>Ofertas</strong>: el texto de ejemplo, el emoji, los colores y los chips de sugerencia.
            </p>

            {/* Placeholder text */}
            <div>
              <Label className="text-xs">Texto dentro de la barra (placeholder)</Label>
              <Input
                value={config.offers_search_placeholder || ''}
                onChange={(e) => updateConfig('offers_search_placeholder', e.target.value)}
                placeholder="Ej: ¿Qué oferta buscas?"
                data-testid="config-offers-placeholder"
              />
            </div>

            {/* Emoji */}
            <div>
              <Label className="text-xs">Emoji de la barra (izquierda)</Label>
              <Input
                value={config.offers_search_emoji || ''}
                onChange={(e) => updateConfig('offers_search_emoji', e.target.value)}
                placeholder="Ej: ✨ 🔍 🛍️ 💥"
                maxLength={4}
                className="w-24 text-lg"
                data-testid="config-offers-emoji"
              />
            </div>

            {/* Colors */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { key: 'offers_bar_bg_color', label: 'Fondo de la barra', def: '#ffffff' },
                { key: 'offers_bar_border_color', label: 'Borde de la barra', def: '#e2e8f0' },
                { key: 'offers_bar_button_bg', label: 'Fondo del botón "Buscar"', def: '#2563eb' },
                { key: 'offers_bar_button_text', label: 'Texto del botón "Buscar"', def: '#ffffff' },
              ].map((c) => (
                <div key={c.key}>
                  <Label className="text-xs">{c.label}</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={config[c.key] || c.def}
                      onChange={(e) => updateConfig(c.key, e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                      data-testid={`config-${c.key}`}
                    />
                    <Input
                      value={config[c.key] || c.def}
                      onChange={(e) => updateConfig(c.key, e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Mercado Libre fallback (no exact match) */}
            <div className="border-t border-slate-100 pt-4 space-y-4">
              <div>
                <Label className="text-xs">Plantilla de búsqueda en Mercado Libre</Label>
                <Input
                  value={config.ml_search_url_template || ''}
                  onChange={(e) => updateConfig('ml_search_url_template', e.target.value)}
                  placeholder="https://listado.mercadolibre.com.mx/{q}"
                  data-testid="config-ml-search-template"
                />
                <p className="text-[11px] text-slate-400 mt-1">Usa <code>{'{q}'}</code> donde va la palabra buscada. Se usa en el botón "Buscar en Mercado Libre" cuando no hay resultado exacto.</p>
              </div>
              <div>
                <Label className="text-xs">Tu perfil/tienda de afiliado en Mercado Libre</Label>
                <Input
                  value={config.ml_affiliate_profile_url || ''}
                  onChange={(e) => updateConfig('ml_affiliate_profile_url', e.target.value)}
                  placeholder="https://www.mercadolibre.com.mx/social/tuusuario"
                  data-testid="config-ml-profile-url"
                />
                <p className="text-[11px] text-slate-400 mt-1">Se muestra como botón "Ver mi tienda". Déjalo vacío para ocultarlo.</p>
              </div>
            </div>

            {/* Suggestion chips */}
            <div className="border-t border-slate-100 pt-4">
              <Label className="text-xs">Chips de sugerencia (debajo de la barra)</Label>
              <div className="flex gap-2 mt-2 mb-3">
                <button
                  type="button"
                  onClick={() => updateConfig('offers_suggestions_mode', 'manual')}
                  data-testid="config-chips-mode-manual"
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${(config.offers_suggestions_mode || 'manual') === 'manual' ? 'bg-royal text-white border-royal' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  Yo los escribo
                </button>
                <button
                  type="button"
                  onClick={() => updateConfig('offers_suggestions_mode', 'popular')}
                  data-testid="config-chips-mode-popular"
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${config.offers_suggestions_mode === 'popular' ? 'bg-royal text-white border-royal' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  Las más buscadas por usuarios
                </button>
              </div>

              {config.offers_suggestions_mode === 'popular' ? (
                <p className="text-[13px] text-slate-500 bg-slate-50 rounded-lg p-3">
                  Se mostrarán automáticamente los términos que más buscan tus usuarios (y que sí encontraron ofertas). No necesitas escribir nada.
                </p>
              ) : (
                <div className="space-y-2">
                  {searchSuggestions.length === 0 && (
                    <p className="text-[13px] text-slate-400">Aún no hay chips. Agrega los que quieras que aparezcan debajo de la barra.</p>
                  )}
                  {searchSuggestions.map((sugg, index) => (
                    <div key={index} className="flex items-center gap-2" data-testid={`config-suggestion-${index}`}>
                      <Input
                        value={sugg}
                        onChange={(e) => updateSearchSuggestion(index, e.target.value)}
                        placeholder="Ej: audífonos baratos, regalo bajo 500..."
                        className="flex-1"
                      />
                      <button
                        onClick={() => removeSearchSuggestion(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        data-testid={`config-suggestion-remove-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <Button onClick={addSearchSuggestion} variant="outline" className="w-full" data-testid="config-suggestion-add">
                    <Plus className="w-4 h-4 mr-2" /> Agregar Chip
                  </Button>
                </div>
              )}
            </div>
          </div>
        </ConfigSection>

        {/* Quick Access Buttons */}
        <ConfigSection icon={Zap} title="Accesos Rápidos a Ofertas">
          <div className="space-y-4 pt-4">
            {quickButtons.map((btn) => (
              <div key={btn.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Switch checked={btn.active} onCheckedChange={(v) => updateQuickButton(btn.id, 'active', v)} />
                  <span className="text-sm font-medium text-slate-700">Activo</span>
                  <button onClick={() => removeQuickButton(btn.id)} className="ml-auto p-1 text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Emoji</Label>
                    <Input value={btn.icon} onChange={(e) => updateQuickButton(btn.id, 'icon', e.target.value)} maxLength={2} className="text-center" />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">Texto</Label>
                    <Input value={btn.text} onChange={(e) => updateQuickButton(btn.id, 'text', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">URL</Label>
                    <Input value={btn.url} onChange={(e) => updateQuickButton(btn.id, 'url', e.target.value)} placeholder="https://..." />
                  </div>
                </div>
                <div className="mt-2">
                  <Label className="text-xs">Color</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={btn.color} onChange={(e) => updateQuickButton(btn.id, 'color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                    <Input value={btn.color} onChange={(e) => updateQuickButton(btn.id, 'color', e.target.value)} className="flex-1" />
                  </div>
                </div>
              </div>
            ))}
            <Button onClick={addQuickButton} variant="outline" className="w-full">
              <Plus className="w-4 h-4 mr-2" /> Agregar Botón
            </Button>
          </div>
        </ConfigSection>

        {/* WhatsApp Share Message */}
        <ConfigSection icon={Share2} title="Mensaje de WhatsApp">
          <div className="space-y-4 pt-4">
            <p className="text-sm text-slate-600">
              Personaliza el mensaje que se envía al compartir un cupón. Variables disponibles: {'{title}'}, {'{code}'}, {'{affiliate_link}'}, {'{site_url}'}
            </p>
            <div>
              <Label>Mensaje de compartir</Label>
              <Textarea 
                value={config.whatsapp_share_message}
                onChange={(e) => updateConfig('whatsapp_share_message', e.target.value)}
                rows={8}
                className="font-mono text-sm"
              />
            </div>
            <div>
              <Label>Link de afiliado</Label>
              <Input 
                value={config.affiliate_link}
                onChange={(e) => updateConfig('affiliate_link', e.target.value)}
                placeholder="https://mercadolibre.com/sec/..."
              />
            </div>
          </div>
        </ConfigSection>

        {/* Grid Configuration */}
        <ConfigSection icon={Grid3X3} title="Configuración del Grid">
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Columnas Móvil</Label>
                <Select value={String(config.grid_mobile_columns)} onValueChange={(v) => updateConfig('grid_mobile_columns', parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 columna</SelectItem>
                    <SelectItem value="2">2 columnas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Columnas Tablet</Label>
                <Select value={String(config.grid_tablet_columns)} onValueChange={(v) => updateConfig('grid_tablet_columns', parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 columna</SelectItem>
                    <SelectItem value="2">2 columnas</SelectItem>
                    <SelectItem value="3">3 columnas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Columnas Desktop</Label>
                <Select value={String(config.grid_desktop_columns)} onValueChange={(v) => updateConfig('grid_desktop_columns', parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 columna</SelectItem>
                    <SelectItem value="2">2 columnas</SelectItem>
                    <SelectItem value="3">3 columnas</SelectItem>
                    <SelectItem value="4">4 columnas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Ordenar cupones por</Label>
              <Select value={config.coupon_sort_by} onValueChange={(v) => updateConfig('coupon_sort_by', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Más recientes</SelectItem>
                  <SelectItem value="copies">Más copiados</SelectItem>
                  <SelectItem value="votes">Más votados</SelectItem>
                  <SelectItem value="trust">Mayor confianza</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </ConfigSection>

        {/* Coupon Tabs */}
        <ConfigSection icon={Grid3X3} title="Pestañas de Cupones">
          <div className="space-y-3 pt-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">Pestañas de la barra inferior (🛒 Tienda, 🏦 Bancarios, etc.). Personaliza emoji, nombre y colores, o agrega más. Los cupones se asignan a una pestaña al crearlos o editarlos.</p>
              <Button data-testid="add-coupon-tab" type="button" variant="outline" size="sm" onClick={addCouponTab} className="flex-shrink-0 ml-2">
                <Plus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            </div>
            {couponTabs.map((tab) => (
              <div key={tab.id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2 flex-wrap">
                <Input
                  data-testid={`coupon-tab-emoji-${tab.id}`}
                  value={tab.emoji}
                  onChange={(e) => updateCouponTab(tab.id, 'emoji', e.target.value)}
                  className="w-16 text-center"
                  placeholder="📌"
                />
                <Input
                  data-testid={`coupon-tab-name-${tab.id}`}
                  value={tab.name}
                  onChange={(e) => updateCouponTab(tab.id, 'name', e.target.value)}
                  className="flex-1 min-w-[120px]"
                  placeholder="Nombre de la pestaña"
                />
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-slate-500">Fondo</span>
                  <input type="color" data-testid={`coupon-tab-color-${tab.id}`} value={tab.color || '#FFD600'} onChange={(e) => updateCouponTab(tab.id, 'color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-slate-500">Texto</span>
                  <input type="color" value={tab.text_color || '#0f172a'} onChange={(e) => updateCouponTab(tab.id, 'text_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                </div>
                <Button
                  data-testid={`coupon-tab-delete-${tab.id}`}
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeCouponTab(tab.id)}
                  className="text-red-500 hover:text-red-700 flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </ConfigSection>

        {/* Community Savings Bar */}
        <ConfigSection icon={BarChart3} title="Barra de Ahorro de la Comunidad">
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Mostrar barra de ahorro</p>
                <p className="text-xs text-slate-500">Aparece junto al botón "Actualizar" en la página de cupones</p>
              </div>
              <Switch data-testid="savings-enabled-switch" checked={config.savings_bar_enabled} onCheckedChange={(v) => updateConfig('savings_bar_enabled', v)} />
            </div>

            {savingsStats && (
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-emerald-600" data-testid="savings-stat-current">${Number(savingsStats.current).toLocaleString('es-MX')}</p>
                  <p className="text-[10px] text-slate-500">Progreso actual ({savingsStats.percent}%)</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-blue-600" data-testid="savings-stat-total">${Number(savingsStats.total_all_time).toLocaleString('es-MX')}</p>
                  <p className="text-[10px] text-slate-500">Ahorro histórico total</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-amber-600" data-testid="savings-stat-goals">{savingsStats.goals_reached}</p>
                  <p className="text-[10px] text-slate-500">Metas alcanzadas</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Título</Label>
                <Input value={config.savings_bar_title} onChange={(e) => updateConfig('savings_bar_title', e.target.value)} placeholder="Ahorro de la comunidad" />
              </div>
              <div>
                <Label>Texto explicativo</Label>
                <Input value={config.savings_bar_text} onChange={(e) => updateConfig('savings_bar_text', e.target.value)} placeholder="Ahorro estimado generado usando los cupones" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Meta de ahorro ($)</Label>
                <Input data-testid="savings-goal-input" type="number" min="1" value={config.savings_goal} onChange={(e) => updateConfig('savings_goal', parseFloat(e.target.value) || 0)} />
              </div>
              <div>
                <Label>Monto inicial ($)</Label>
                <Input data-testid="savings-initial-input" type="number" min="0" value={config.savings_initial} onChange={(e) => updateConfig('savings_initial', parseFloat(e.target.value) || 0)} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Emoji de progreso</Label>
                <Input value={config.savings_emoji_progress} onChange={(e) => updateConfig('savings_emoji_progress', e.target.value)} className="text-center" placeholder="🐼" />
              </div>
              <div>
                <Label>Emoji de meta</Label>
                <Input value={config.savings_emoji_goal} onChange={(e) => updateConfig('savings_emoji_goal', e.target.value)} className="text-center" placeholder="🎋" />
              </div>
              <div>
                <Label>Color de la barra</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.savings_bar_color} onChange={(e) => updateConfig('savings_bar_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={config.savings_bar_color} onChange={(e) => updateConfig('savings_bar_color', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <div>
                <p className="font-medium text-slate-700">Panda animado con imágenes (estilo Pac-Man)</p>
                <p className="text-xs text-slate-500">Sube las dos imágenes (boca cerrada y boca abierta) para animar el panda alternándolas. Si dejas alguna vacía, se usa el emoji de progreso.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ImageUploader
                  currentImage={config.savings_panda_img_closed}
                  onImageChange={(url) => updateConfig('savings_panda_img_closed', url)}
                  label="Imagen boca cerrada"
                  removeBg={true}
                />
                <ImageUploader
                  currentImage={config.savings_panda_img_open}
                  onImageChange={(url) => updateConfig('savings_panda_img_open', url)}
                  label="Imagen boca abierta"
                  removeBg={true}
                />
              </div>
              <p className="text-xs text-emerald-600">✨ El fondo blanco se elimina automáticamente al subir estas imágenes.</p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Emojis del camino (el panda se los come)</Label>
                <Input data-testid="savings-path-emojis-input" value={config.savings_path_emojis} onChange={(e) => updateConfig('savings_path_emojis', e.target.value)} className="text-center" placeholder="🪙 💵 🎟️" />
                <p className="text-xs text-slate-500 mt-1">Puedes poner uno o varios (se alternan). Vacío = sin emojis.</p>
              </div>
              <div>
                <Label>Cantidad de emojis en la barra</Label>
                <Input data-testid="savings-path-count-input" type="number" min="1" max="20" value={config.savings_path_emoji_count} onChange={(e) => updateConfig('savings_path_emoji_count', parseInt(e.target.value) || 8)} />
              </div>
            </div>
            </div>

            <div>
              <Label>Periodo del progreso</Label>
              <Select value={config.savings_period} onValueChange={(v) => updateConfig('savings_period', v)}>
                <SelectTrigger data-testid="savings-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Diario (se reinicia cada día)</SelectItem>
                  <SelectItem value="weekly">Semanal (se reinicia cada lunes)</SelectItem>
                  <SelectItem value="monthly">Mensual (se reinicia el día 1)</SelectItem>
                  <SelectItem value="total">Acumulado (no se reinicia solo)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <p className="font-medium text-slate-700">Mostrar monto acumulado y meta</p>
              <Switch checked={config.savings_show_amount} onCheckedChange={(v) => updateConfig('savings_show_amount', v)} />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <p className="font-medium text-slate-700">Mostrar texto explicativo</p>
              <Switch checked={config.savings_show_text} onCheckedChange={(v) => updateConfig('savings_show_text', v)} />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Reinicio automático al alcanzar la meta</p>
                <p className="text-xs text-slate-500">Si está apagado, la barra se queda llena hasta que la reinicies manualmente</p>
              </div>
              <Switch data-testid="savings-auto-reset-switch" checked={config.savings_auto_reset} onCheckedChange={(v) => updateConfig('savings_auto_reset', v)} />
            </div>

            <Button data-testid="savings-reset-button" type="button" variant="outline" onClick={handleResetSavings} className="w-full text-red-600 border-red-200 hover:bg-red-50">
              Reiniciar progreso ahora
            </Button>
            <p className="text-xs text-slate-500">💡 El ahorro se suma cada vez que alguien pulsa "Copiar y Canjear". Configura el "Ahorro estimado por uso" en cada cupón. En productos, se usa automáticamente la diferencia entre precio original y precio de oferta.</p>
          </div>
        </ConfigSection>

        {/* Help Button Configuration */}
        <ConfigSection icon={MessageSquare} title="Botón de Ayuda (Instrucciones)">
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Mostrar botón de ayuda</p>
                <p className="text-xs text-slate-500">Barra en la parte superior de toda la página</p>
              </div>
              <Switch data-testid="help-enabled-switch" checked={config.help_button_enabled} onCheckedChange={(v) => updateConfig('help_button_enabled', v)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Texto del botón</Label>
                <Input data-testid="help-button-text-input" value={config.help_button_text} onChange={(e) => updateConfig('help_button_text', e.target.value)} placeholder="❓ ¿Cómo usar los cupones?" />
              </div>
              <div>
                <Label>Color de la barra</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.help_button_color} onChange={(e) => updateConfig('help_button_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={config.help_button_color} onChange={(e) => updateConfig('help_button_color', e.target.value)} />
                </div>
              </div>
            </div>

            <div>
              <Label>Título del bloc de notas</Label>
              <Input data-testid="help-title-input" value={config.help_title} onChange={(e) => updateConfig('help_title', e.target.value)} placeholder="Cómo usar los cupones" />
            </div>

            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-medium text-slate-700">Contenido de las instrucciones</p>
                  <p className="text-xs text-slate-500">Agrega bloques de texto e imágenes en el orden que quieras (se ven como un bloc de notas)</p>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="add-help-text" type="button" variant="outline" size="sm" onClick={() => addHelpBlock('text')}>
                    <Plus className="w-4 h-4 mr-1" /> Texto
                  </Button>
                  <Button data-testid="add-help-image" type="button" variant="outline" size="sm" onClick={() => addHelpBlock('image')}>
                    <Plus className="w-4 h-4 mr-1" /> Imagen
                  </Button>
                </div>
              </div>
              {helpBlocks.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-2">Sin contenido. Agrega bloques de texto o imágenes.</p>
              )}
              {helpBlocks.map((block, idx) => (
                <div key={block.id} className="bg-slate-50 rounded-lg p-3 flex gap-2">
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button type="button" onClick={() => moveHelpBlock(block.id, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" title="Subir">
                      <ChevronUp className="w-4 h-4 text-slate-600" />
                    </button>
                    <button type="button" onClick={() => moveHelpBlock(block.id, 1)} disabled={idx === helpBlocks.length - 1} className="p-1 rounded hover:bg-slate-200 disabled:opacity-30" title="Bajar">
                      <ChevronDown className="w-4 h-4 text-slate-600" />
                    </button>
                  </div>
                  <div className="flex-1 min-w-0">
                    {block.type === 'image' ? (
                      <ImageUploader
                        currentImage={block.content}
                        onImageChange={(url) => updateHelpBlock(block.id, url)}
                        label="Imagen de las instrucciones"
                      />
                    ) : (
                      <Textarea
                        data-testid={`help-block-text-${block.id}`}
                        value={block.content}
                        onChange={(e) => updateHelpBlock(block.id, e.target.value)}
                        placeholder="Escribe un paso o explicación..."
                        rows={3}
                      />
                    )}
                  </div>
                  <Button
                    data-testid={`help-block-delete-${block.id}`}
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeHelpBlock(block.id)}
                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </ConfigSection>

        {/* Community Chat Room */}
        <ConfigSection icon={MessageCircle} title="WhatsApp Flotante" defaultOpen={true}>
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Mostrar botón flotante de WhatsApp</p>
                <p className="text-xs text-slate-500">Ícono verde en la esquina inferior derecha con link directo a tu WhatsApp</p>
              </div>
              <Switch data-testid="floating-whatsapp-enabled-switch" checked={config.floating_whatsapp_enabled !== false} onCheckedChange={(v) => updateConfig('floating_whatsapp_enabled', v)} />
            </div>

            <div>
              <Label>URL de WhatsApp</Label>
              <Input
                data-testid="floating-whatsapp-url-input"
                value={config.floating_whatsapp_url || ''}
                onChange={(e) => updateConfig('floating_whatsapp_url', e.target.value)}
                placeholder="https://chat.whatsapp.com/tu-grupo o https://wa.me/521XXXXXXXXXX"
              />
              <p className="text-xs text-slate-500 mt-1">
                Ejemplos: <code>https://wa.me/5215512345678</code> (chat personal) o <code>https://chat.whatsapp.com/XXXXXX</code> (grupo). Si dejas vacío, el botón no se muestra.
              </p>
            </div>
          </div>
        </ConfigSection>

        {/* Validation Buttons */}
        <ConfigSection icon={ThumbsUp} title="Botones de Validación">
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Mostrar botón "Me funcionó" 👍</p>
                <p className="text-xs text-slate-500">Permite a usuarios votar positivamente</p>
              </div>
              <Switch checked={config.show_worked_button} onCheckedChange={(v) => updateConfig('show_worked_button', v)} />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Mostrar botón "No me funcionó" 👎</p>
                <p className="text-xs text-slate-500">Permite a usuarios votar negativamente</p>
              </div>
              <Switch checked={config.show_not_worked_button} onCheckedChange={(v) => updateConfig('show_not_worked_button', v)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Emoji "Me funcionó"</Label>
                <Input data-testid="action-emoji-worked-input" value={config.action_emoji_worked} onChange={(e) => updateConfig('action_emoji_worked', e.target.value)} className="text-center" placeholder="👍" />
              </div>
              <div>
                <Label>Emoji "No me funcionó"</Label>
                <Input data-testid="action-emoji-not-worked-input" value={config.action_emoji_not_worked} onChange={(e) => updateConfig('action_emoji_not_worked', e.target.value)} className="text-center" placeholder="👎" />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Mostrar contador de copias</p>
              </div>
              <Switch checked={config.show_copies_count} onCheckedChange={(v) => updateConfig('show_copies_count', v)} />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Mostrar contador de validaciones</p>
              </div>
              <Switch checked={config.show_validations_count} onCheckedChange={(v) => updateConfig('show_validations_count', v)} />
            </div>
          </div>
        </ConfigSection>

        {/* Banner Configuration */}
        <ConfigSection icon={Image} title="Configuración del Banner">
          <div className="space-y-4 pt-4">
            <div>
              <Label>Título del Banner</Label>
              <Input value={config.banner_title} onChange={(e) => updateConfig('banner_title', e.target.value)} />
              <p className="text-xs text-slate-500 mt-1">Tip: escribe <span className="font-mono font-bold">{'{fecha}'}</span> y se reemplazará automáticamente cada día. Ej: "Cupones Hoy {'{fecha}'}" → "Cupones Hoy {new Date().getDate()} de {['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'][new Date().getMonth()]}"</p>
            </div>

            <div>
              <Label>Subtítulo</Label>
              <Input value={config.banner_subtitle} onChange={(e) => updateConfig('banner_subtitle', e.target.value)} />
              <p className="text-xs text-slate-500 mt-1">También acepta <span className="font-mono font-bold">{'{fecha}'}</span></p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Color del título</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.banner_title_color || '#000000'} onChange={(e) => updateConfig('banner_title_color', e.target.value)} className="w-10 h-9 rounded cursor-pointer" data-testid="input-banner-title-color" />
                  <Input value={config.banner_title_color || '#000000'} onChange={(e) => updateConfig('banner_title_color', e.target.value)} className="text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Color del subtítulo</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.banner_subtitle_color || '#000000'} onChange={(e) => updateConfig('banner_subtitle_color', e.target.value)} className="w-10 h-9 rounded cursor-pointer" data-testid="input-banner-subtitle-color" />
                  <Input value={config.banner_subtitle_color || '#000000'} onChange={(e) => updateConfig('banner_subtitle_color', e.target.value)} className="text-xs" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Mostrar subtítulo</p>
                <p className="text-xs text-slate-500">Actívalo para mostrar el subtítulo debajo del título</p>
              </div>
              <Switch data-testid="banner-subtitle-enabled-switch" checked={config.banner_subtitle_enabled !== false} onCheckedChange={(v) => updateConfig('banner_subtitle_enabled', v)} />
            </div>

            <ImageUploader
              currentImage={config.banner_image_url}
              onImageChange={(url) => updateConfig('banner_image_url', url)}
              label="Imagen del Banner (opcional)"
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tamaño de la imagen</Label>
                <Select value={config.banner_image_size} onValueChange={(v) => updateConfig('banner_image_size', v)}>
                  <SelectTrigger data-testid="banner-image-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Pequeña</SelectItem>
                    <SelectItem value="medium">Mediana</SelectItem>
                    <SelectItem value="large">Grande</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Altura del banner</Label>
                <Select value={config.banner_height || 'auto'} onValueChange={(v) => updateConfig('banner_height', v)}>
                  <SelectTrigger data-testid="banner-height-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Pequeña</SelectItem>
                    <SelectItem value="auto">Automática</SelectItem>
                    <SelectItem value="medium">Mediana</SelectItem>
                    <SelectItem value="large">Grande</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <p className="font-medium text-slate-700">Mostrar subtítulo en móvil</p>
              <Switch checked={config.banner_show_description_mobile} onCheckedChange={(v) => updateConfig('banner_show_description_mobile', v)} />
            </div>

            <p className="text-xs text-slate-500 italic">El botón "¿Cómo usar los cupones?" se configura en la sección <span className="font-semibold">"Botón de Ayuda (Instrucciones)"</span>.</p>
          </div>
        </ConfigSection>

        {/* Buscador de Cupones (Calculadora) */}
        <ConfigSection icon={Calculator} title="Buscador de Cupones (Calculadora)">
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Activar buscador</p>
                <p className="text-xs text-slate-500">Muestra la calculadora debajo del banner</p>
              </div>
              <Switch data-testid="finder-enabled-switch" checked={config.finder_enabled !== false} onCheckedChange={(v) => updateConfig('finder_enabled', v)} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Emoji del ícono (opcional)</Label>
                <Input
                  data-testid="finder-emoji-input"
                  value={config.finder_emoji || ''}
                  onChange={(e) => updateConfig('finder_emoji', e.target.value)}
                  placeholder="Ej: 🧮 (déjalo vacío para usar el ícono por defecto)"
                  maxLength={4}
                />
                <p className="text-xs text-slate-500 mt-1">Reemplaza el ícono de calculadora al lado del input</p>
              </div>
              <div>
                <Label>Placeholder del input</Label>
                <Input value={config.finder_placeholder || ''} onChange={(e) => updateConfig('finder_placeholder', e.target.value)} placeholder="¿Cuánto cuesta tu compra?" />
              </div>
              <div>
                <Label>Texto del botón</Label>
                <Input value={config.finder_button_text || ''} onChange={(e) => updateConfig('finder_button_text', e.target.value)} placeholder="Encontrar" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Fondo del botón</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.finder_button_bg || '#3B82F6'} onChange={(e) => updateConfig('finder_button_bg', e.target.value)} className="w-10 h-9 rounded cursor-pointer" />
                  <Input value={config.finder_button_bg || '#3B82F6'} onChange={(e) => updateConfig('finder_button_bg', e.target.value)} className="text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Texto del botón (color)</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.finder_button_text_color || '#ffffff'} onChange={(e) => updateConfig('finder_button_text_color', e.target.value)} className="w-10 h-9 rounded cursor-pointer" />
                  <Input value={config.finder_button_text_color || '#ffffff'} onChange={(e) => updateConfig('finder_button_text_color', e.target.value)} className="text-xs" />
                </div>
              </div>
            </div>

            <ImageUploader
              currentImage={config.finder_image_url}
              onImageChange={(url) => updateConfig('finder_image_url', url)}
              label="Imagen (mascota/panda) del resultado"
            />

            <div className="border border-slate-200 rounded-lg p-3 space-y-3">
              <p className="font-medium text-slate-700 text-sm">Recuadro de resultado (MEJOR OPCIÓN)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Texto del badge</Label>
                  <Input value={config.finder_best_badge_text || ''} onChange={(e) => updateConfig('finder_best_badge_text', e.target.value)} placeholder="MEJOR OPCIÓN" />
                </div>
                <div>
                  <Label className="text-xs">Sufijo del ahorro</Label>
                  <Input value={config.finder_savings_suffix || ''} onChange={(e) => updateConfig('finder_savings_suffix', e.target.value)} placeholder="de ahorro" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Color badge</Label>
                  <input type="color" value={config.finder_best_badge_bg || '#10b981'} onChange={(e) => updateConfig('finder_best_badge_bg', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
                <div>
                  <Label className="text-xs">Texto badge</Label>
                  <input type="color" value={config.finder_best_badge_text_color || '#ffffff'} onChange={(e) => updateConfig('finder_best_badge_text_color', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
                <div>
                  <Label className="text-xs">Borde recuadro</Label>
                  <input type="color" value={config.finder_border_color || '#10b981'} onChange={(e) => updateConfig('finder_border_color', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Color del monto de ahorro</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.finder_savings_text_color || '#2563eb'} onChange={(e) => updateConfig('finder_savings_text_color', e.target.value)} className="w-10 h-9 rounded cursor-pointer" />
                  <Input value={config.finder_savings_text_color || '#2563eb'} onChange={(e) => updateConfig('finder_savings_text_color', e.target.value)} className="text-xs" />
                </div>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg p-3 space-y-3">
              <p className="font-medium text-slate-700 text-sm">Botón "Copiar y canjear" del resultado</p>
              <div>
                <Label className="text-xs">Texto</Label>
                <Input value={config.finder_copy_button_text || ''} onChange={(e) => updateConfig('finder_copy_button_text', e.target.value)} placeholder="Copiar y canjear" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Fondo</Label>
                  <input type="color" value={config.finder_copy_button_bg || '#fbbf24'} onChange={(e) => updateConfig('finder_copy_button_bg', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
                <div>
                  <Label className="text-xs">Texto</Label>
                  <input type="color" value={config.finder_copy_button_text_color || '#000000'} onChange={(e) => updateConfig('finder_copy_button_text_color', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg p-3 space-y-3">
              <p className="font-medium text-slate-700 text-sm">Recuadro &quot;Te faltan ... para desbloquear&quot;</p>
              <div>
                <Label className="text-xs">Texto principal</Label>
                <Textarea
                  data-testid="finder-hint-gap-text-input"
                  value={config.finder_hint_gap_text || ''}
                  onChange={(e) => updateConfig('finder_hint_gap_text', e.target.value)}
                  rows={2}
                  placeholder="Solo agrega {gap} más para el cupón de compra mínima de {min}"
                />
                <p className="text-xs text-slate-500 mt-1">Usa <code>{'{gap}'}</code> (lo que falta), <code>{'{min}'}</code> (compra mínima) y <code>{'{savings}'}</code> (ahorro) como variables</p>
              </div>
              <div>
                <Label className="text-xs">Texto secundario</Label>
                <Textarea
                  data-testid="finder-hint-extra-text-input"
                  value={config.finder_hint_extra_text || ''}
                  onChange={(e) => updateConfig('finder_hint_extra_text', e.target.value)}
                  rows={2}
                  placeholder="Productos extra por sólo {gap}"
                />
                <p className="text-xs text-slate-500 mt-1">Usa <code>{'{gap}'}</code> como variable</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Fondo</Label>
                  <input type="color" value={config.finder_hint_bg || '#EFF6FF'} onChange={(e) => updateConfig('finder_hint_bg', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
                <div>
                  <Label className="text-xs">Texto</Label>
                  <input type="color" value={config.finder_hint_text_color || '#1e293b'} onChange={(e) => updateConfig('finder_hint_text_color', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
                <div>
                  <Label className="text-xs">Acento (montos)</Label>
                  <input type="color" value={config.finder_hint_accent_color || '#2563eb'} onChange={(e) => updateConfig('finder_hint_accent_color', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
              </div>
            </div>

            <div>
              <Label>Texto cuando NO hay cupón aplicable</Label>
              <Textarea value={config.finder_no_result_text || ''} onChange={(e) => updateConfig('finder_no_result_text', e.target.value)} rows={2} placeholder="No encontramos un cupón para ese monto. Prueba con un monto mayor." />
            </div>

            <div className="border border-slate-200 rounded-lg p-3 space-y-3">
              <p className="font-medium text-slate-700 text-sm">Leyenda "Combínalo con cupón bancario"</p>
              <p className="text-xs text-slate-500">Aparece cuando hay cupones bancarios activos, para invitar al usuario a combinar.</p>
              <div>
                <Label className="text-xs">Texto</Label>
                <Input value={config.finder_bank_combo_text || ''} onChange={(e) => updateConfig('finder_bank_combo_text', e.target.value)} placeholder="💳 Combínalo con un cupón bancario si tienes disponible" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Fondo</Label>
                  <input type="color" value={config.finder_bank_combo_bg || '#FEF3C7'} onChange={(e) => updateConfig('finder_bank_combo_bg', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
                <div>
                  <Label className="text-xs">Texto</Label>
                  <input type="color" value={config.finder_bank_combo_text_color || '#78350f'} onChange={(e) => updateConfig('finder_bank_combo_text_color', e.target.value)} className="w-full h-9 rounded cursor-pointer" />
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-500 italic">La calculadora busca <span className="font-semibold">solo en cupones de tienda</span>. Si hay cupones bancarios activos, se muestra la leyenda de combinación.</p>
          </div>
        </ConfigSection>

        {/* Dark Mode Configuration */}
        <ConfigSection icon={Moon} title="Modo Oscuro">
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Permitir modo oscuro a los usuarios</p>
                <p className="text-xs text-slate-500">Muestra el botón de luna/sol en la barra superior</p>
              </div>
              <Switch data-testid="dark-mode-enabled-switch" checked={config.dark_mode_enabled} onCheckedChange={(v) => updateConfig('dark_mode_enabled', v)} />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Modo oscuro por defecto</p>
                <p className="text-xs text-slate-500">Los visitantes nuevos verán la página en oscuro</p>
              </div>
              <Switch data-testid="dark-mode-default-switch" checked={config.dark_mode_default} onCheckedChange={(v) => updateConfig('dark_mode_default', v)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Color de fondo</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.dark_bg_color} onChange={(e) => updateConfig('dark_bg_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={config.dark_bg_color} onChange={(e) => updateConfig('dark_bg_color', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Color de tarjetas</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.dark_card_bg_color} onChange={(e) => updateConfig('dark_card_bg_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={config.dark_card_bg_color} onChange={(e) => updateConfig('dark_card_bg_color', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Color de texto</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.dark_text_color} onChange={(e) => updateConfig('dark_text_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={config.dark_text_color} onChange={(e) => updateConfig('dark_text_color', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Color de barra superior</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.dark_header_color} onChange={(e) => updateConfig('dark_header_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={config.dark_header_color} onChange={(e) => updateConfig('dark_header_color', e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        </ConfigSection>

        {/* Button Configuration */}
        <ConfigSection icon={MousePointer} title="Configuración del Botón Principal">
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Texto del botón</Label>
                <Input value={config.button_text} onChange={(e) => updateConfig('button_text', e.target.value)} />
              </div>
              <div>
                <Label>Texto del botón de producto/oferta</Label>
                <Input data-testid="product-button-text-input" value={config.product_button_text} onChange={(e) => updateConfig('product_button_text', e.target.value)} placeholder="Ver Oferta" />
              </div>
              <div>
                <Label>Segundos antes de redirigir</Label>
                <Input type="number" min="1" max="30" value={config.button_redirect_seconds} onChange={(e) => updateConfig('button_redirect_seconds', parseInt(e.target.value) || 5)} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Mensaje al copiar</Label>
                <Input value={config.button_copied_message} onChange={(e) => updateConfig('button_copied_message', e.target.value)} />
              </div>
              <div>
                <Label>Mensaje de redirección</Label>
                <Input value={config.button_redirect_message} onChange={(e) => updateConfig('button_redirect_message', e.target.value)} />
              </div>
            </div>
          </div>
        </ConfigSection>

        {/* Site Messages */}
        <ConfigSection icon={MessageSquare} title="Mensajes del Sitio">
          <div className="space-y-4 pt-4">
            <div>
              <Label>Mensaje cuando no hay cupones</Label>
              <Input value={config.msg_no_coupons} onChange={(e) => updateConfig('msg_no_coupons', e.target.value)} />
            </div>
            <div>
              <Label>Descripción cuando no hay cupones</Label>
              <Textarea value={config.msg_no_coupons_desc} onChange={(e) => updateConfig('msg_no_coupons_desc', e.target.value)} />
            </div>
            <div>
              <Label>Mensaje de agradecimiento por voto</Label>
              <Input value={config.msg_validation_thanks} onChange={(e) => updateConfig('msg_validation_thanks', e.target.value)} />
            </div>
            <div>
              <Label>Mensaje "Ya usaste este cupón"</Label>
              <Input value={config.msg_already_used} onChange={(e) => updateConfig('msg_already_used', e.target.value)} />
            </div>
          </div>
        </ConfigSection>

        {/* Default Colors */}
        <ConfigSection icon={Palette} title="Colores Predeterminados de Cupones">
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Fondo del cupón</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.default_coupon_bg_color} onChange={(e) => updateConfig('default_coupon_bg_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                  <Input value={config.default_coupon_bg_color} onChange={(e) => updateConfig('default_coupon_bg_color', e.target.value)} className="text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Fondo del descuento</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.default_coupon_discount_bg} onChange={(e) => updateConfig('default_coupon_discount_bg', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                  <Input value={config.default_coupon_discount_bg} onChange={(e) => updateConfig('default_coupon_discount_bg', e.target.value)} className="text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Fondo del botón</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.default_coupon_button_bg} onChange={(e) => updateConfig('default_coupon_button_bg', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                  <Input value={config.default_coupon_button_bg} onChange={(e) => updateConfig('default_coupon_button_bg', e.target.value)} className="text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Texto del botón</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.default_coupon_button_text} onChange={(e) => updateConfig('default_coupon_button_text', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                  <Input value={config.default_coupon_button_text} onChange={(e) => updateConfig('default_coupon_button_text', e.target.value)} className="text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Texto del cupón</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.default_coupon_text_color} onChange={(e) => updateConfig('default_coupon_text_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                  <Input value={config.default_coupon_text_color} onChange={(e) => updateConfig('default_coupon_text_color', e.target.value)} className="text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Fondo compra mínima</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.default_coupon_min_purchase_bg} onChange={(e) => updateConfig('default_coupon_min_purchase_bg', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                  <Input value={config.default_coupon_min_purchase_bg} onChange={(e) => updateConfig('default_coupon_min_purchase_bg', e.target.value)} className="text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Borde punteado del cupón</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={config.default_coupon_border_color || '#FFD600'} onChange={(e) => updateConfig('default_coupon_border_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" data-testid="input-coupon-border-color" />
                  <Input value={config.default_coupon_border_color || '#FFD600'} onChange={(e) => updateConfig('default_coupon_border_color', e.target.value)} className="text-xs" />
                </div>
              </div>
            </div>

            {/* Neon glow effect — separate controls for border and button */}
            <div className="mt-4 p-3 rounded-xl border border-slate-200 bg-slate-50 space-y-4">
              <p className="text-sm font-bold text-slate-700">Contorno neón ✨</p>

              {/* Border neon */}
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-semibold">Neón del borde del cupón</Label>
                    <p className="text-xs text-slate-500">Resplandor alrededor de la tarjeta.</p>
                  </div>
                  <Switch
                    data-testid="neon-border-switch"
                    checked={!!config.neon_border_enabled}
                    onCheckedChange={(v) => updateConfig('neon_border_enabled', v)}
                  />
                </div>
                {config.neon_border_enabled && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <input type="color" value={config.neon_border_color || '#00E5FF'} onChange={(e) => updateConfig('neon_border_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" data-testid="input-neon-border-color" />
                      <Input value={config.neon_border_color || '#00E5FF'} onChange={(e) => updateConfig('neon_border_color', e.target.value)} className="text-xs" />
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {['#00E5FF', '#39FF14', '#FF10F0', '#FFD600', '#FF3131', '#8A2BE2'].map((c) => (
                        <button key={c} type="button" onClick={() => updateConfig('neon_border_color', c)} className="w-7 h-7 rounded-full border-2 border-white shadow" style={{ backgroundColor: c, boxShadow: `0 0 8px ${c}` }} title={c} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Button neon */}
              <div className="pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-semibold">Neón del botón "Copiar y Canjear"</Label>
                    <p className="text-xs text-slate-500">Resplandor alrededor del botón principal.</p>
                  </div>
                  <Switch
                    data-testid="neon-button-switch"
                    checked={!!config.neon_button_enabled}
                    onCheckedChange={(v) => updateConfig('neon_button_enabled', v)}
                  />
                </div>
                {config.neon_button_enabled && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <input type="color" value={config.neon_button_color || '#00E5FF'} onChange={(e) => updateConfig('neon_button_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" data-testid="input-neon-button-color" />
                      <Input value={config.neon_button_color || '#00E5FF'} onChange={(e) => updateConfig('neon_button_color', e.target.value)} className="text-xs" />
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {['#00E5FF', '#39FF14', '#FF10F0', '#FFD600', '#FF3131', '#8A2BE2'].map((c) => (
                        <button key={c} type="button" onClick={() => updateConfig('neon_button_color', c)} className="w-7 h-7 rounded-full border-2 border-white shadow" style={{ backgroundColor: c, boxShadow: `0 0 8px ${c}` }} title={c} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Pulse toggle */}
              {(config.neon_border_enabled || config.neon_button_enabled) && (
                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <div>
                    <Label className="text-sm font-semibold">Parpadeo en degradado (última hora)</Label>
                    <p className="text-xs text-slate-500">El neón pulsa suavemente cuando falta 1 hora o menos.</p>
                  </div>
                  <Switch
                    data-testid="neon-pulse-switch"
                    checked={config.neon_pulse_enabled !== false}
                    onCheckedChange={(v) => updateConfig('neon_pulse_enabled', v)}
                  />
                </div>
              )}
            </div>
          </div>
        </ConfigSection>

        {/* General Settings */}
        <ConfigSection icon={Settings} title="Configuración General">
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nombre del Sitio</Label>
                <Input value={config.site_name} onChange={(e) => updateConfig('site_name', e.target.value)} />
              </div>
              <div>
                <Label>Enlace de WhatsApp</Label>
                <Input value={config.whatsapp_link} onChange={(e) => updateConfig('whatsapp_link', e.target.value)} placeholder="https://whatsapp.com/..." />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Color de la barra superior</Label>
                <div className="flex items-center gap-2">
                  <input type="color" data-testid="header-bg-color-picker" value={config.header_bg_color} onChange={(e) => updateConfig('header_bg_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={config.header_bg_color} onChange={(e) => updateConfig('header_bg_color', e.target.value)} />
                </div>
              </div>
              <div>
                <Label>Color del título (OFERTOPOLIS)</Label>
                <div className="flex items-center gap-2">
                  <input type="color" data-testid="header-text-color-picker" value={config.header_text_color} onChange={(e) => updateConfig('header_text_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={config.header_text_color} onChange={(e) => updateConfig('header_text_color', e.target.value)} />
                </div>
              </div>
            </div>

            <ImageUploader
              currentImage={config.logo_url}
              onImageChange={(url) => updateConfig('logo_url', url)}
              label="Logo del Sitio"
            />

            <div className="border border-slate-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-700">Botones del menú superior</p>
                  <p className="text-xs text-slate-500">Botones junto a "Cupones" en la barra superior: emoji, texto, enlace, colores, negritas y tipo de letra</p>
                </div>
                <Button data-testid="add-header-button" type="button" variant="outline" size="sm" onClick={addHeaderButton}>
                  <Plus className="w-4 h-4 mr-1" /> Agregar
                </Button>
              </div>
              {headerButtons.length === 0 && (
                <p className="text-xs text-slate-400 text-center py-2">No hay botones. Haz clic en "Agregar" para crear uno.</p>
              )}
              {headerButtons.map((btn) => (
                <div key={btn.id} className="bg-slate-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid={`header-button-emoji-${btn.id}`}
                      value={btn.emoji}
                      onChange={(e) => updateHeaderButton(btn.id, 'emoji', e.target.value)}
                      className="w-16 text-center"
                      placeholder="🔗"
                    />
                    <Input
                      data-testid={`header-button-text-${btn.id}`}
                      value={btn.text}
                      onChange={(e) => updateHeaderButton(btn.id, 'text', e.target.value)}
                      className="flex-1"
                      placeholder="Nombre del botón"
                    />
                    <Input
                      data-testid={`header-button-url-${btn.id}`}
                      value={btn.url}
                      onChange={(e) => updateHeaderButton(btn.id, 'url', e.target.value)}
                      className="flex-1"
                      placeholder="https://..."
                    />
                    <Button
                      data-testid={`header-button-delete-${btn.id}`}
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeHeaderButton(btn.id)}
                      className="text-red-500 hover:text-red-700 flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-slate-500">Fondo</span>
                      <input type="color" value={btn.bg_color || '#ffffff'} onChange={(e) => updateHeaderButton(btn.id, 'bg_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-slate-500">Texto</span>
                      <input type="color" value={btn.text_color || '#1e293b'} onChange={(e) => updateHeaderButton(btn.id, 'text_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-slate-500">Negritas</span>
                      <Switch checked={!!btn.bold} onCheckedChange={(v) => updateHeaderButton(btn.id, 'bold', v)} />
                    </div>
                    <div className="flex items-center gap-1.5 flex-1 min-w-[140px]">
                      <span className="text-xs text-slate-500">Letra</span>
                      <Select value={btn.font || 'nunito'} onValueChange={(v) => updateHeaderButton(btn.id, 'font', v)}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nunito">Nunito (normal)</SelectItem>
                          <SelectItem value="fredoka">Fredoka (redondeada)</SelectItem>
                          <SelectItem value="serif">Serif (clásica)</SelectItem>
                          <SelectItem value="mono">Monoespaciada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ConfigSection>


      </div>
    </div>
  );
};

export default AdminConfiguracion;
